"use client"

import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef } from 'react'
import type { Swiper as SwiperInstance } from 'swiper'
import { EffectFade, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { HeroSlide } from '@/lib/mcare-data'

export function HeroSwiper({ slides }: { slides: HeroSlide[] }) {
  const swiperRef = useRef<SwiperInstance | null>(null)

  useEffect(() => {
    if (slides.length <= 1) return undefined
    const timer = window.setInterval(() => {
      swiperRef.current?.slideNext(850)
    }, 5200)
    return () => window.clearInterval(timer)
  }, [slides.length])

  return <section className="relative overflow-hidden bg-[#1A1A1A]">
    <Swiper
      modules={[EffectFade, Pagination]}
      loop={false}
      rewind={slides.length > 1}
      effect="fade"
      speed={850}
      fadeEffect={{ crossFade: true }}
      onSwiper={(swiper) => { swiperRef.current = swiper }}
      pagination={{ clickable: true }}
      className="mcare-hero-swiper"
    >
      {slides.map((slide) => <SwiperSlide key={slide.id}>
        <article className="relative min-h-[680px] overflow-hidden md:min-h-[760px]">
          <Image src={slide.image} alt={slide.alt} fill sizes="100vw" priority={slide.id === 'music-room'} className="mcare-hero-image object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/82 via-black/42 to-black/14" />
          <div className="absolute inset-x-0 bottom-0 top-0 flex items-center px-5 py-20">
            <div className="mx-auto w-full max-w-7xl">
              <div className="mcare-hero-copy max-w-3xl rounded-lg border border-white/10 bg-[#1A1A1A]/72 p-7 shadow-2xl shadow-black/30 backdrop-blur-sm md:p-10">
                <p className="mcare-hero-eyebrow text-sm font-black uppercase tracking-[0.24em] text-[#FF4500]">{slide.eyebrow}</p>
                <h1 className="mcare-hero-title mt-5 text-3xl font-black leading-[1.22] tracking-[-0.055em] text-[#F9FAFB] sm:text-4xl md:text-5xl lg:text-6xl">{slide.title}</h1>
                <p className="mcare-hero-description mt-7 max-w-2xl text-base font-bold leading-7 text-[#E5E5E5] md:text-lg md:leading-8">{slide.description}</p>
                <div className="mcare-hero-actions mt-9 flex flex-wrap gap-3">
                  <Link href={slide.primaryCta.href} className="rounded-full bg-[#FF4500] px-6 py-4 font-black text-white shadow-xl shadow-[#FF4500]/20">{slide.primaryCta.label}</Link>
                  <Link href={slide.secondaryCta.href} className="rounded-full border border-[#FF4500] bg-[#2A2A2A]/90 px-6 py-4 font-black text-[#F9FAFB]">{slide.secondaryCta.label}</Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </SwiperSlide>)}
    </Swiper>
  </section>
}
